import React, { memo, forwardRef, useImperativeHandle, useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { setProfile } from "store/slices/profile";
import { promptAlertMessage } from "store/slices/alert";
import validateFileSize from "common/validate-file-size";
import serveRequestErrors from "common/serve-request-errors";
import converReadableFileSize from "common/convert-readable-file-size";
import AppButton from "components/app-button";
import uploadIcon from "assets/images/upload-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";

const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png"];

const AVATAR_ERRORS = {
	HR_USER_00004: ERRORS.AVATAR_FILE_EMPTY,
	HR_USER_00005: ERRORS.AVATAR_FILE_TYPE,
	HR_LEAVE_000012: ERRORS.AVATAR_FILE_TYPE,
};

const validateFileType = (file) => {
	if (!file?.name) return true;
	return ALLOWED_EXTENSIONS.includes(file.name.split(".").pop().toLowerCase());
};

export const AppUploadAvatarModal = (props, ref) => {
	const dispatch = useDispatch();
	const profile = useSelector((state) => state.profile);
	const uploadInputRef = useRef();
	const [visible, setVisible] = useState(false);
	const [previewUrl, setPreviewUrl] = useState("");
	const initialValues = useMemo(() => ({ file: null }), []);
	const profileName = useMemo(() => profile?.nickName?.split(" ")?.[0]?.[0] || "H", [profile.nickName]);

	//prettier-ignore
	const formik = useFormik({
		initialValues,
		validationSchema: yup.object({
			file: yup.mixed().nullable().required(ERRORS.REQUIRED)
				.test("fileType", ERRORS.AVATAR_FILE_TYPE, validateFileType)
				.test("fileSize", ERRORS.AVATAR_FILE_SIZE, (v) => validateFileSize(v)),
		}),
		onSubmit: (values) => {
			onHandleSubmit(values);
		},
	});
	const file = formik.values.file;

	useEffect(() => {
		if (!file) {
			setPreviewUrl("");
			return undefined;
		}

		const url = URL.createObjectURL(file);
		setPreviewUrl(url);

		return () => URL.revokeObjectURL(url);
	}, [file]);

	const onHandleShow = useCallback(() => {
		setVisible(true);
	}, []);

	const onHandleDismiss = useCallback(() => {
		setVisible(false);
		formik.resetForm();
		if (uploadInputRef.current) uploadInputRef.current.value = "";
	}, [formik]);

	//prettier-ignore
	const onHandleChange = useCallback((event) => {
		formik.setFieldTouched("file", true, false);
		formik.setFieldValue("file", event.target.files[0] || null);
	}, [formik]);

	//prettier-ignore
	const onHandleRemoveFile = useCallback(() => {
		formik.setFieldValue("file", null);
		uploadInputRef.current.value = "";
	}, [formik]);

	const onHandleUploadFile = useCallback(() => {
		uploadInputRef.current.click();
	}, []);

	//prettier-ignore
	const onHandleSubmit = useCallback(async (values) => {
		let response = null;

		try {
			const formData = new FormData();
			formData.append("file", values.file);

			response = await api.post.general.uploadAvatar(formData);
		} catch (error) {
			const message = AVATAR_ERRORS[error?.response?.data?.errorCode];

			if (message) dispatch(promptAlertMessage({ error: true, message }));
			else serveRequestErrors(error);
		} finally {
			formik.setSubmitting(false);
		}

		if (response?.avatar) {
			dispatch(setProfile({ avatar: response.avatar }));
			dispatch(promptAlertMessage({ message: "Profile picture has been updated successfully" }));
			onHandleDismiss();
		}
	}, [dispatch, formik, onHandleDismiss]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	const displayImage = previewUrl || profile.avatar;

	return (
		<Modal classes={{ root: "app-upload-avatar-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="upload-avatar-modal" aria-describedby="upload-avatar-modal">
			<form className="upload-avatar-modal" onSubmit={formik.handleSubmit}>
				<p className="upload-avatar-modal__title">Change Photo</p>

				<div className="upload-avatar-modal__preview">
					{displayImage ? <img src={displayImage} alt="profile" /> : <p className="upload-avatar-modal__initial">{profileName}</p>}
				</div>

				<div className="upload-avatar-modal__input">
					<button type="button" className="upload-avatar-modal__upload-button" disabled={formik.isSubmitting} onClick={onHandleUploadFile}>
						{file?.name || "Upload"}
						<img className="upload-avatar-modal__icon" src={uploadIcon} alt="upload" />
					</button>

					<p className="upload-avatar-modal__label">{converReadableFileSize(file?.size, true) || "-"}</p>

					{file && (
						<button type="button" className="upload-avatar-modal__delete" disabled={formik.isSubmitting} onClick={onHandleRemoveFile}>
							<img src={removeIcon} alt="remove" />
						</button>
					)}

					<input type="file" name="file" accept="image/jpeg, image/jpg, image/png" hidden ref={uploadInputRef} onChange={onHandleChange} />
				</div>

				<p className="upload-avatar-modal__description">Please ensure that your image is in the correct format (jpg, png) with file size not exceeding 5MB.</p>

				{formik.touched.file && formik.errors.file &&<div className="upload-avatar-modal__error">{formik.errors.file}</div>}

				<div className="upload-avatar-modal__button-container">
					<AppButton type="button" label="Cancel" outline disabled={formik.isSubmitting} onClick={onHandleDismiss} />
					<AppButton type="submit" label="Upload" disabled={formik.isSubmitting || !file} />
				</div>
			</form>
		</Modal>
	);
};

export default memo(forwardRef(AppUploadAvatarModal));

AppUploadAvatarModal.propTypes = {
	ref: PropTypes.object,
};
